/**
 * Project + revision handoff loader. When the SLD editor is opened
 * with `?project=<qid>` (and optionally `&rev=<id>`), this module
 * resolves which topology to seed the editor with:
 *
 *   1. ?project=X&rev=Y         → load that exact revision
 *   2. ?project=X (no rev)      → list revisions for X. If any exist
 *                                 load the latest. If none, generate
 *                                 a fresh "auto" revision from
 *                                 project-topology.php and save it
 *                                 as the project's first revision.
 *   3. Neither                  → don't fetch anything; the editor
 *                                 falls back to localStorage as
 *                                 before (standalone mode).
 *
 * Revision metadata is set on the editor store via
 * `setProjectContext` so the toolbar's Revisions dropdown knows
 * what's currently loaded.
 *
 * Failures fall through silently — the editor renders whatever
 * persisted state localStorage has, which keeps the standalone
 * tool usable even if the SP backend is unreachable.
 */
import { useEditorStore } from '../store/editor';
import { sldApiUrl } from './sldApiUrl';
import {
  listRevisions,
  loadRevision,
  createRevision,
} from './revisions';
import type { Topology } from '../types/topology';

/**
 * Compute a small at-a-glance summary from a freshly-loaded
 * project topology — used to populate the "Linked Project" card
 * in the SLD's right sidebar. The summary is a snapshot of WHAT
 * SP HAS, distinct from whatever the user has currently in the
 * SLD revision (which they may have edited).
 */
export function summariseProjectTopology(t: Topology): NonNullable<ReturnType<typeof useEditorStore.getState>['projectSummary']> {
  const project = t.project ?? { address: '' };
  // The address field in project mode is "Customer Name — full
  // address" (joined by an em-dash in project-topology.php).
  // Split it so the card can show the customer name as the title
  // line and the address as the subtitle.
  let customerName = '';
  let address = (project.address ?? '').toString();
  const dashIdx = address.indexOf(' — ');
  if (dashIdx > 0) {
    customerName = address.slice(0, dashIdx).trim();
    address = address.slice(dashIdx + 3).trim();
  }

  // Resolve a panel-type lookup so we can find the pmax for each
  // inverter's panels. Falls back to topology.panels (the legacy
  // single-type field) when an inverter has no explicit
  // panelTypeId match.
  const panelTypeById = new Map<string, { pmax: number }>();
  for (const pt of t.panelTypes ?? []) panelTypeById.set(pt.id, pt);
  const legacyPmax = t.panels?.pmax ?? 0;

  let panels = 0;
  let panelKwp = 0;
  let inverterKwAc = 0;

  for (const inv of t.inverters ?? []) {
    inverterKwAc += (inv.capacity ?? 0) / 1000;
    const ptypePmax = (inv.panelTypeId && panelTypeById.get(inv.panelTypeId)?.pmax) || legacyPmax;
    for (const m of inv.mppts ?? []) {
      for (const s of m.strings ?? []) {
        const count = s.panels ?? 0;
        panels    += count;
        panelKwp  += count * ptypePmax;
      }
    }
  }

  let batteryKwh = 0;
  for (const b of t.batteries ?? []) {
    batteryKwh += b.capacity ?? 0;
  }

  return {
    customerName,
    address,
    panels,
    panelKwp:     panelKwp / 1000,   // W → kW
    inverters:    (t.inverters ?? []).length,
    inverterKwAc,
    batteries:    (t.batteries ?? []).length,
    batteryKwh,
    capturedAt:   Date.now(),
  };
}

interface UrlParams {
  projectId: string;
  revisionId: number | null;
}

function readUrlParams(): UrlParams | null {
  if (typeof window === 'undefined') return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }
  const projectId = (params.get('project') ?? '').trim();
  if (!/^\d+$/.test(projectId)) return null;
  const revRaw = params.get('rev');
  const revisionId = revRaw && /^\d+$/.test(revRaw) ? parseInt(revRaw, 10) : null;
  return { projectId, revisionId };
}

/**
 * Look at the URL; if `?project=<id>` is present, resolve the right
 * revision and load its topology into the editor store. Returns a
 * promise that resolves once we've decided. Safe to fire-and-forget
 * from main.tsx.
 */
export async function loadProjectFromUrlIfPresent(): Promise<void> {
  const params = readUrlParams();
  if (!params) return;

  const { projectId, revisionId } = params;

  try {
    // Path A — caller asked for a specific revision id.
    if (revisionId !== null) {
      const rev = await loadRevision(revisionId);
      applyToStore(rev.topology, projectId, rev.id, rev.label, rev.source);
      return;
    }

    // Path B — no revision id; resolve based on what exists.
    const list = await listRevisions(projectId);
    if (list.length > 0) {
      // Load the most recently updated revision.
      const latest = list[0];
      const full = await loadRevision(latest.id);
      applyToStore(full.topology, projectId, full.id, full.label, full.source);
      return;
    }

    // Path C — no revisions yet for this project. Generate the
    // initial auto revision from project-topology.php and save it
    // back so subsequent opens can use Path B's fast path.
    const seedTopology = await fetchProjectTopology(projectId);
    if (!seedTopology) return;

    const created = await createRevision({
      qid: projectId,
      topology: seedTopology,
      source: 'auto',
      label: 'Initial auto-generated',
    });
    applyToStore(seedTopology, projectId, created.id, created.label, 'auto');
  } catch (err) {
    // 402 from project-topology.php = the user's plan / project tier
    // doesn't entitle them to the SLD Editor on this project. Show a
    // paywall message and bounce them to the upgrade page rather than
    // silently dropping into standalone mode (which would look like
    // the project just failed to load).
    if (err instanceof SldUpgradeRequiredError) {
      // Use the styled in-app dialog (same as every other modal in the
      // editor) instead of the native window.confirm — the browser
      // chrome dialog looks alien and breaks the visual flow.
      // ConfirmDialog is mounted at root in main.tsx so it can render
      // even though we're called pre-render (the promise resolves once
      // the user clicks, by which point the React tree is mounted).
      const goUpgrade = await useEditorStore.getState().askConfirm({
        title: 'SLD Editor — upgrade required',
        message: err.message,
        confirmLabel: 'View pricing',
        cancelLabel: 'Not now',
        tone: 'info',
      });
      if (goUpgrade) {
        window.location.href = err.upgradeUrl;
      } else {
        // User declined — close the tab if we opened it ourselves,
        // otherwise leave them on a clean landing page. We can't
        // reliably window.close() unless we were script-opened, so
        // route to the SLD marketing landing as a soft fallback.
        window.location.href = '/sld/dist/index.html';
      }
      return;
    }
    console.warn('[SLD] Project handoff failed; falling back to local state.', err);
  }
}

/**
 * Hand the topology + revision metadata to the editor store. Wipes
 * any localStorage-restored nodes/edges (loadTopology does this) so
 * autoLayout regenerates against the new topology.
 */
function applyToStore(
  topology: Topology,
  projectId: string,
  revisionId: number,
  revisionLabel: string,
  revisionSource: 'auto' | 'manual',
  projectSnapshot?: Topology,
): void {
  const store = useEditorStore.getState();
  store.loadTopology(topology);
  store.setProjectContext({ projectId, revisionId, revisionLabel, revisionSource });
  // Capture the project summary from whichever topology represents
  // SP's current state. When we load from a revision, that's the
  // revision's stored topology (which was either auto-generated or
  // manually saved); for a fresh seed it's the project-topology
  // response. `projectSnapshot` lets the caller pass a different
  // source than `topology` if they have a freshly-fetched one.
  store.setProjectSummary(summariseProjectTopology(projectSnapshot ?? topology));
}

/**
 * Fetch the SP-generated topology (no revision involved). Used only
 * to seed a brand-new project the first time it's opened — and by
 * the toolbar's "New from project data" action later.
 */
/**
 * Thrown when the backend returns 402 (Payment Required) — the
 * project exists but the user's plan / project tier doesn't entitle
 * them to use the SLD Editor on it. Caller decides how to surface it
 * (we currently show a paywall alert + redirect to the landing page).
 */
export class SldUpgradeRequiredError extends Error {
  readonly currentTier: string;
  readonly requiredTier: string;
  readonly upgradeUrl: string;
  constructor(payload: { message?: string; currentTier?: string; requiredTier?: string; upgradeUrl?: string }) {
    super(payload.message || 'SLD Editor requires an upgraded plan or project tier.');
    this.name = 'SldUpgradeRequiredError';
    this.currentTier  = payload.currentTier  || 'standard';
    this.requiredTier = payload.requiredTier || 'indepth';
    this.upgradeUrl   = payload.upgradeUrl   || 'https://www.solarproof.com.au/pricing_tiers';
  }
}

export async function fetchProjectTopology(projectId: string): Promise<Topology | null> {
  let res: Response;
  try {
    // cache: 'no-store' is critical here. The user clicks "From
    // project" specifically to pull the LATEST quote data — a
    // cached response defeats the entire point of the action.
    // Belt-and-braces with the no-store Cache-Control header on
    // project-topology.php's response.
    res = await fetch(
      sldApiUrl(`project-topology.php?qid=${encodeURIComponent(projectId)}`),
      { cache: 'no-store' },
    );
  } catch (err) {
    console.warn('[SLD] project-topology fetch failed:', err);
    return null;
  }
  // 402 = the credit-tier gate denied access. Surface it as a
  // typed error so the caller can show a proper paywall instead of
  // a silent fallback to standalone mode (which would just confuse
  // the user — "why is my project blank?").
  if (res.status === 402) {
    let payload: { message?: string; currentTier?: string; requiredTier?: string; upgradeUrl?: string } = {};
    try { payload = await res.json(); } catch { /* keep defaults */ }
    throw new SldUpgradeRequiredError(payload);
  }
  if (!res.ok) {
    console.warn(`[SLD] project-topology returned ${res.status}`);
    return null;
  }
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    return null;
  }
  if (!isTopologyShape(data)) {
    console.warn('[SLD] project-topology response did not look like a Topology:', data);
    return null;
  }
  return data;
}

function isTopologyShape(x: unknown): x is Topology {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    'project' in o &&
    'panels' in o &&
    Array.isArray((o as { inverters?: unknown }).inverters) &&
    Array.isArray((o as { batteries?: unknown }).batteries)
  );
}
